import _ from 'lodash';
import {
  ITask,
  TaskDirection,
  TaskOptions,
  TaskState,
  TaskType,
} from './types';
import { tasksState } from './state';
import appManager from '@app/appManager';

class Task implements ITask {
  private _id: string;
  private _started = Date.now();
  private _state: TaskState;
  private _tenantId: string | null;
  private _type: TaskType = 'voice';
  private _direction: TaskDirection = 'inbound';
  private _displayName: string | undefined;
  private _from: string = '';
  private _attributes: Record<string, any> = {};
  private _wrapUpEndsAt: number | null;
  private _queueName: string | undefined;
  private _queueId: string | undefined;
  private _isInternal: boolean;

  constructor(options: TaskOptions) {
    const {
      id,
      tenantId,
      from,
      startedAt = Date.now(),
      type = 'voice',
      direction = 'inbound',
      state = TaskState.Pending,
      displayName = '',
      attributes = {},
      wrapUpEndsAt = null,
      queueName = undefined,
      queueId = undefined,
      isInternal = false,
    } = options;

    this._id = id;
    this._tenantId = tenantId ?? null;
    this._displayName = displayName;
    this._from = from;
    this._type = type;
    this._direction = direction;
    this._state = state;
    this._attributes = attributes;
    this._wrapUpEndsAt = wrapUpEndsAt;
    this._queueName = queueName;
    this._queueId = queueId;
    this._started = startedAt;
    this._isInternal = isInternal;
  }

  public get id(): string {
    return this._id;
  }

  public set id(id: string) {
    if (this._id === id) {
      return;
    }
    this._id = id;
    this.markAsUpdated();
  }

  public get type(): TaskType {
    return this._type;
  }

  public get direction(): TaskDirection {
    return this._direction;
  }

  public get state(): TaskState {
    return this._state;
  }

  public set state(state: TaskState) {
    if (this._state === state) {
      return;
    }
    this._state = state;
    appManager.events.emit(`task.${state.toLowerCase()}`, this);
    this.markAsUpdated();
  }

  public get startedAt(): Date {
    return new Date(this._started);
  }

  public set startedAt(value: Date) {
    if (value.getTime() === this._started) {
      return;
    }
    this._started = value.getTime();
    this.markAsUpdated();
  }

  public get tenantId(): string {
    return this._tenantId ?? '';
  }

  public get displayName(): string {
    return this._displayName || this._from;
  }

  public set displayName(name: string | undefined) {
    if (this._displayName === name) {
      return;
    }
    this._displayName = name;
    this.markAsUpdated();
  }

  public get from(): string {
    return this._from;
  }

  public get attributes(): Record<string, any> {
    return this._attributes;
  }

  public set attributes(value: Record<string, any>) {
    if (_.isEqual(this._attributes, value)) {
      return;
    }
    this._attributes = value;
    this.markAsUpdated();
  }

  public get wrapUpEndsAt(): number | null {
    return this._wrapUpEndsAt;
  }

  public set wrapUpEndsAt(value: number | null) {
    if (this._wrapUpEndsAt === value) {
      return;
    }
    this._wrapUpEndsAt = value;
    this.markAsUpdated();
  }

  public get queueName(): string | undefined {
    return this._queueName;
  }

  public set queueName(value: string | undefined) {
    if (this._queueName === value) {
      return;
    }
    this._queueName = value;
    this.markAsUpdated();
  }

  public get queueId(): string | undefined {
    return this._queueId;
  }

  public set queueId(value: string | undefined) {
    if (this._queueId === value) {
      return;
    }
    this._queueId = value;
    this.markAsUpdated();
  }

  public get isInternal(): boolean {
    return this._isInternal;
  }

  public get isOutbound(): boolean {
    return this._direction === 'outbound';
  }

  private markAsUpdated() {
    tasksState.version++;
    appManager.events.emit(`task.updated`, this);
  }
}

export default Task;
